import { useEffect, useRef } from "react";

// Decorative hero background: a slow-drifting network of nodes with small
// pulses travelling along the links. Pauses off-screen; static for reduced motion.

const LINK_DISTANCE = 150;
const MAX_PULSES = 10;

export default function HeroCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return undefined;

    // Static frame for reduced motion or data-saver; fewer nodes on small screens.
    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || Boolean(navigator.connection?.saveData);
    const small = window.matchMedia?.("(max-width: 700px)").matches;
    let width = 0;
    let height = 0;
    let nodes = [];
    let pulses = [];
    let frame = 0;
    let running = false;

    function resize() {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.round(Math.min(small ? 28 : 70, Math.max(small ? 16 : 24, (width * height) / 16000)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: Math.random() * 1.4 + 0.8,
        phase: Math.random() * Math.PI * 2,
      }));
      pulses = [];
    }

    function neighbours(node) {
      return nodes.filter((other) => other !== node && Math.hypot(other.x - node.x, other.y - node.y) < LINK_DISTANCE);
    }

    function spawnPulse() {
      const from = nodes[Math.floor(Math.random() * nodes.length)];
      const options = neighbours(from);
      if (!options.length) return;
      pulses.push({ from, to: options[Math.floor(Math.random() * options.length)], t: 0 });
    }

    function draw(time) {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < nodes.length; i += 1) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j += 1) {
          const b = nodes[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist > LINK_DISTANCE) continue;
          ctx.strokeStyle = `rgba(227, 191, 122, ${(1 - dist / LINK_DISTANCE) * 0.16})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      nodes.forEach((node) => {
        const glow = 0.45 + Math.sin(time * 0.0012 + node.phase) * 0.25;
        ctx.fillStyle = `rgba(246, 226, 179, ${glow})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.fill();
      });

      pulses.forEach((pulse) => {
        const x = pulse.from.x + (pulse.to.x - pulse.from.x) * pulse.t;
        const y = pulse.from.y + (pulse.to.y - pulse.from.y) * pulse.t;
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, 10);
        gradient.addColorStop(0, "rgba(246, 226, 179, 0.95)");
        gradient.addColorStop(1, "rgba(227, 191, 122, 0)");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function step(time) {
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < -20) node.x = width + 20;
        if (node.x > width + 20) node.x = -20;
        if (node.y < -20) node.y = height + 20;
        if (node.y > height + 20) node.y = -20;
      });

      pulses.forEach((pulse) => {
        pulse.t += 0.012;
      });
      pulses = pulses.filter((pulse) => pulse.t < 1);
      if (pulses.length < MAX_PULSES && Math.random() < 0.06) spawnPulse();

      draw(time);
      frame = requestAnimationFrame(step);
    }

    function start() {
      if (running || reduced) return;
      running = true;
      frame = requestAnimationFrame(step);
    }

    function stop() {
      running = false;
      cancelAnimationFrame(frame);
    }

    resize();
    draw(0);

    const resizeObserver = new ResizeObserver(() => {
      resize();
      draw(performance.now());
    });
    resizeObserver.observe(canvas);

    const viewObserver = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()));
    viewObserver.observe(canvas);

    return () => {
      stop();
      resizeObserver.disconnect();
      viewObserver.disconnect();
    };
  }, []);

  return <canvas ref={ref} className="hero-canvas" aria-hidden="true" />;
}
